import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Note } from '../components/ui/Note';
import { BILLING_LIVE, BILLING_PENDING_NOTE, PAID_PLAN_TRIAL, PAID_PLAN_TRIAL_DAYS } from '../config';
import { isPaidPlanId, PLAN_CURRENCY, PLAN_TIERS, type PaidPlanId } from '../data/plans';
import { useAccount, useCheckout, useJoinWaitlist } from '../hooks/useAccount';
import { useDocumentHead } from '../hooks/useDocumentHead';
import { ApiError } from '../lib/api';

/**
 * Public pricing page. Free → magic-link signup. Paid → server-created Checkout
 * Session (client only sends the plan name).
 *
 * When `BILLING_LIVE` is false the paid CTA is waitlist capture instead, tagged
 * with the tier that was clicked. Prices, cards and legal terms stay visible.
 */
export function PricingPage() {
    useDocumentHead({
        title: 'Pricing · ActualSold',
        description:
            'Free 100 requests / mo. Paid plans from $9 USD / mo. One request is one live eBay page up to 200 rows. Commercial use on every tier.',
        canonicalPath: '/pricing',
    });

    const account = useAccount();
    const checkout = useCheckout();
    const waitlist = useJoinWaitlist();

    const [pendingPlan, setPendingPlan] = useState<PaidPlanId | null>(null);
    /** Which card has its email field open. Only ever one. */
    const [notifyPlan, setNotifyPlan] = useState<PaidPlanId | null>(null);
    const [email, setEmail] = useState('');
    const [joinedPlans, setJoinedPlans] = useState<readonly PaidPlanId[]>([]);

    const signedIn = Boolean(account.data);
    const currentPlan = account.data?.user.plan;

    function startCheckout(plan: PaidPlanId) {
        setPendingPlan(plan);
        checkout.mutate(plan, {
            onSettled: () => setPendingPlan(null),
        });
    }

    function openNotify(plan: PaidPlanId) {
        waitlist.reset();
        setNotifyPlan(plan);
        setEmail(account.data?.user.email ?? '');
    }

    function submitNotify(plan: PaidPlanId) {
        const trimmed = email.trim();
        if (!trimmed) return;

        waitlist.mutate(
            { email: trimmed, source: `pricing:${plan}` },
            {
                onSuccess: () => {
                    setJoinedPlans((plans) => (plans.includes(plan) ? plans : [...plans, plan]));
                    setNotifyPlan(null);
                    setEmail('');
                },
            },
        );
    }

    const checkoutError =
        checkout.error instanceof ApiError
            ? checkout.error.message
            : checkout.error
              ? 'Something went wrong starting checkout.'
              : null;

    const waitlistError =
        waitlist.error instanceof ApiError
            ? waitlist.error.message
            : waitlist.error
              ? 'Could not add that address. Check it and try again.'
              : null;

    return (
        <div className="space-y-8 pb-8">
            <header className="space-y-3">
                <p className="eyebrow text-text-muted">Pricing</p>
                <h1 className="text-title">Simple monthly plans</h1>
                <p className="max-w-xl text-text-muted">
                    One request is one live eBay page of up to 200 rows, whether it returns 10 or
                    200. Rate limit is 60/min on every plan. Commercial use included.
                </p>
                <p className="text-sm text-text-muted">
                    All prices in {PLAN_CURRENCY}, billed monthly, cancel any time.
                    {PAID_PLAN_TRIAL
                        ? ` Sign in free, then upgrade for a ${PAID_PLAN_TRIAL_DAYS}-day free trial on your first paid plan (card on file, cancel before it ends and you are not charged). Guests pay from day one.`
                        : null}
                </p>
            </header>

            {!BILLING_LIVE ? <Note>{BILLING_PENDING_NOTE}</Note> : null}

            {checkoutError ? <Note tone="warn">{checkoutError}</Note> : null}

            {/*
              "Popular" used to own the border weight while "Your current plan"
              was a quiet footnote, so signed-in free users thought they were on
              Starter. Current plan is the primary frame once we know it.
            */}
            {signedIn && currentPlan ? (
                <p className="text-sm text-text">
                    You are on{' '}
                    <span className="font-medium">
                        {PLAN_TIERS.find((t) => t.id === currentPlan)?.name ?? currentPlan}
                    </span>
                    {currentPlan === 'free' ? ' (100 requests / mo).' : '.'}
                </p>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
                {PLAN_TIERS.map((tier) => {
                    const isCurrent = signedIn && currentPlan === tier.id;
                    const isPaid = isPaidPlanId(tier.id);
                    const planId = tier.id as PaidPlanId;
                    const isBusy = checkout.isPending && pendingPlan === tier.id;
                    const hasJoined = isPaid && joinedPlans.includes(planId);
                    const isNotifyOpen = notifyPlan === tier.id;
                    // Featured only when nothing else claims the card.
                    const showPopular = Boolean(tier.featured) && !isCurrent;

                    return (
                        <article
                            key={tier.id}
                            className={`flex flex-col rounded-lg border p-5 sm:p-6 ${
                                isCurrent
                                    ? 'border-2 border-secondary bg-surface shadow-[inset_0_0_0_1px_var(--color-secondary)]'
                                    : showPopular
                                      ? 'border-border-strong bg-surface'
                                      : 'border-border bg-surface'
                            }`}
                        >
                            <div className="flex items-baseline justify-between gap-2">
                                <h2 className="eyebrow text-text">{tier.name}</h2>
                                {isCurrent ? (
                                    <span className="bg-secondary px-1.5 py-0.5 font-mono text-xs text-bg uppercase">
                                        Your plan
                                    </span>
                                ) : showPopular ? (
                                    <span className="bg-primary-soft px-1.5 py-0.5 font-mono text-xs text-primary uppercase">
                                        Popular
                                    </span>
                                ) : null}
                            </div>
                            <p className="mt-3 text-3xl font-bold tracking-[-0.02em] text-text">
                                {tier.priceLabel}
                                <span className="ml-1 text-sm font-normal text-text-muted">{tier.priceDetail}</span>
                            </p>
                            <p className="mt-1 font-mono text-xs text-text-muted">{tier.requests}</p>
                            <p className="mt-4 flex-1 text-sm text-text-muted">{tier.blurb}</p>

                            <div className="mt-6">
                                {isCurrent ? (
                                    <Link
                                        to="/app"
                                        className="inline-flex w-full items-center justify-center rounded-md border border-secondary bg-secondary px-4 py-2.5 text-sm font-medium text-bg hover:opacity-90"
                                    >
                                        Open console
                                    </Link>
                                ) : isPaid && !BILLING_LIVE ? (
                                    hasJoined ? (
                                        <p className="text-sm text-text-muted">
                                            On the list. We'll email you when {tier.name} opens.
                                        </p>
                                    ) : isNotifyOpen ? (
                                        <div className="space-y-2">
                                            <Input
                                                type="email"
                                                label={`Email for ${tier.name}`}
                                                hideLabel
                                                placeholder="you@company.com"
                                                value={email}
                                                onChange={setEmail}
                                                autoFocus
                                                autoComplete="email"
                                                disabled={waitlist.isPending}
                                                invalid={Boolean(waitlistError)}
                                            />
                                            {waitlistError ? <Note tone="warn">{waitlistError}</Note> : null}
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    type="button"
                                                    disabled={waitlist.isPending || !email.trim()}
                                                    onClick={() => submitNotify(planId)}
                                                >
                                                    {waitlist.isPending ? 'Adding…' : 'Notify me'}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="quiet"
                                                    disabled={waitlist.isPending}
                                                    onClick={() => setNotifyPlan(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            block
                                            variant={tier.featured ? 'primary' : 'secondary'}
                                            onClick={() => openNotify(planId)}
                                        >
                                            {`Notify me when ${tier.name} opens`}
                                        </Button>
                                    )
                                ) : isPaid ? (
                                    <Button
                                        type="button"
                                        block
                                        disabled={checkout.isPending}
                                        onClick={() => startCheckout(planId)}
                                    >
                                        {isBusy ? 'Redirecting…' : `Upgrade to ${tier.name}`}
                                    </Button>
                                ) : signedIn ? (
                                    <p className="text-sm text-text-muted">No card. Available with every paid plan too.</p>
                                ) : (
                                    <Link
                                        to="/signin"
                                        className="inline-flex w-full items-center justify-center rounded-md border border-border-strong bg-surface px-4 py-2.5 text-sm font-medium text-text hover:border-secondary"
                                    >
                                        Start free
                                    </Link>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            {BILLING_LIVE ? (
                <p className="text-sm text-text-muted">
                    Already paying?{' '}
                    {signedIn ? (
                        <Link to="/app" className="text-primary underline">
                            Open the console
                        </Link>
                    ) : (
                        <Link to="/signin" className="text-primary underline">
                            Sign in
                        </Link>
                    )}
                    . Card updates and cancellation live in{' '}
                    <span className="text-text">Manage billing</span> once you have a paid account.
                </p>
            ) : (
                <p className="text-sm text-text-muted">
                    The free tier needs no card and is available now, {' '}
                    {signedIn ? (
                        <Link to="/app" className="text-primary underline">
                            open the console
                        </Link>
                    ) : (
                        <Link to="/signin" className="text-primary underline">
                            sign in to start
                        </Link>
                    )}
                    . When paid plans open, subscriptions are monthly, cancel any time from{' '}
                    <span className="text-text">Manage billing</span>; see{' '}
                    <Link to="/refunds" className="text-primary underline">
                        refunds
                    </Link>{' '}
                    and{' '}
                    <Link to="/terms" className="text-primary underline">
                        terms
                    </Link>
                    .
                </p>
            )}
        </div>
    );
}
